import axios from "axios";

const API_URL = "https://srinivasafleet.com/api";

const authHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    "";

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

export const getCurrentUserId = () => {
  return (
    localStorage.getItem("user_id") ||
    localStorage.getItem("id") ||
    null
  );
};

export const fetchCurrentUser = async (userId = null) => {
  const id = userId || getCurrentUserId();

  if (!id) {
    throw new Error("Current user ID not found");
  }

  try {
    const response = await axios.get(
      `${API_URL}/staff/${id}`,
      {
        headers: authHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch current user:",
      error
    );

    return {
      user_id: localStorage.getItem("user_id"),
      full_name: localStorage.getItem("full_name"),
      role_name: localStorage.getItem("role_name"),
      mobile: localStorage.getItem("mobile"),
    };
  }
};

export const fetchNotifications = async (userId = null) => {
  const id = userId || getCurrentUserId();

  if (!id) {
    return [];
  }

  try {
    const response = await axios.get(
      `${API_URL}/notifications`,
      {
        params: {
          user_id: Number(id),
        },
        headers: authHeaders(),
      }
    );

    const data = response.data;

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.notifications)) {
      return data.notifications;
    }

    if (Array.isArray(data?.results)) {
      return data.results;
    }

    return [];
  } catch (error) {
    console.error(
      "Failed to fetch notifications:",
      error
    );

    return [];
  }
};

export const subscribeToNotifications = (
  userId,
  onNotification
) => {
  if (!userId) {
    return () => {};
  }

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    "";

  if (!token) {
    return () => {};
  }

  const url =
    `${API_URL}/notifications/stream` +
    `?user_id=${encodeURIComponent(Number(userId))}` +
    `&token=${encodeURIComponent(token)}`;

  let eventSource = null;

  try {
    eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (onNotification && data) {
          onNotification(data);
        }
      } catch (error) {
        console.error(
          "Failed to parse notification event:",
          error
        );
      }
    };

    eventSource.addEventListener(
      "notification",
      (event) => {
        try {
          const data = JSON.parse(event.data);

          if (onNotification) {
            onNotification(data);
          }
        } catch (error) {
          console.error(
            "Failed to parse notification event:",
            error
          );
        }
      }
    );

    eventSource.onerror = (error) => {
      console.error(
        "Notification SSE error:",
        error
      );
    };
  } catch (error) {
    console.error(
      "Unable to subscribe to notifications:",
      error
    );
  }

  return () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  };
};

export const markNotificationRead = async (
  notificationId
) => {
  if (!notificationId) {
    return null;
  }

  try {
    const response = await axios.patch(
      `${API_URL}/notifications/${notificationId}/read`,
      {},
      {
        headers: authHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "Failed to mark notification read:",
      error
    );

    throw error;
  }
};

export const markAllNotificationsRead = async (
  userId = null
) => {
  const id = userId || getCurrentUserId();

  if (!id) {
    return null;
  }

  try {
    const response = await axios.patch(
      `${API_URL}/notifications/mark-all-read`,
      {
        user_id: Number(id),
      },
      {
        headers: authHeaders(),
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "Failed to mark all notifications read:",
      error
    );

    throw error;
  }
};

export const globalSearch = async (query) => {
  const cleanedQuery = String(query || "").trim();

  if (cleanedQuery.length < 2) {
    return {
      query: cleanedQuery,
      results: [],
    };
  }

  try {
    const response = await axios.get(
      `${API_URL}/global-search/`,
      {
        params: {
          q: cleanedQuery,
          limit: 12,
        },
        headers: authHeaders(),
      }
    );

    if (Array.isArray(response.data)) {
      return {
        query: cleanedQuery,
        results: response.data,
      };
    }

    return {
      query:
        response.data?.query || cleanedQuery,
      results: Array.isArray(
        response.data?.results
      )
        ? response.data.results
        : [],
    };
  } catch (error) {
    console.error(
      "Global search failed:",
      error
    );

    throw error;
  }
};

export const safeGlobalSearch = async (
  query
) => {
  try {
    return await globalSearch(query);
  } catch (error) {
    console.error(
      "Safe global search failed:",
      error
    );

    return {
      query: String(query || "").trim(),
      results: [],
    };
  }
};

export default {
  getCurrentUserId,
  fetchCurrentUser,
  fetchNotifications,
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  globalSearch,
  safeGlobalSearch,
};